// WebGL2 post-processing pipeline.
// Pipeline: Canvas2D source → bright-pass → 2x blur (bloom) → composite (bloom + chromatic
// aberration + scanlines + CRT curvature + vignette + film grain).
//
// Falls back to passthrough (Canvas2D blit) when WebGL2 is unavailable.

export interface PostFxSettings {
  enabled: boolean;
  crt: number;          // 0..1 scanline intensity
  curvature: number;    // 0..1 barrel curvature
  bloom: number;        // 0..1
  bloomThreshold: number; // 0..1
  chromatic: number;    // 0..1
  grain: number;        // 0..1
  vignette: number;     // 0..1
  glow: number;         // 0..1 — additive screen glow
  scanlineSize: number; // px between dark scanlines
}

export const DEFAULT_POSTFX: PostFxSettings = {
  enabled: true,
  // Near-ambient default — the post-FX should season the picture, not bury it.
  // Heavier values were drowning the UI and washing out cell colors. These
  // can all be cranked up in Settings → Video.
  crt: 0.08,
  curvature: 0.01,
  bloom: 0.28,
  bloomThreshold: 0.80,
  chromatic: 0.06,
  grain: 0.08,
  vignette: 0.30,
  glow: 0.20,
  scanlineSize: 3,
};

const VS = `#version 300 es
in vec2 a_pos;
in vec2 a_uv;
out vec2 v_uv;
void main(){
  v_uv = a_uv;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FS_BRIGHT = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
uniform float u_threshold;
out vec4 frag;
void main(){
  vec3 c = texture(u_src, v_uv).rgb;
  float luma = dot(c, vec3(0.299, 0.587, 0.114));
  float t = max(0.0, luma - u_threshold) / max(0.0001, 1.0 - u_threshold);
  frag = vec4(c * t, 1.0);
}`;

const FS_BLUR = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
uniform vec2 u_dir; // (1,0) for horizontal, (0,1) for vertical
uniform vec2 u_texel;
out vec4 frag;
void main(){
  // 9-tap Gaussian, sigma ~ 4 px
  float w[5];
  w[0] = 0.227027;
  w[1] = 0.1945946;
  w[2] = 0.1216216;
  w[3] = 0.054054;
  w[4] = 0.016216;
  vec3 sum = texture(u_src, v_uv).rgb * w[0];
  for (int i = 1; i < 5; i++) {
    vec2 off = u_dir * u_texel * float(i);
    sum += texture(u_src, v_uv + off).rgb * w[i];
    sum += texture(u_src, v_uv - off).rgb * w[i];
  }
  frag = vec4(sum, 1.0);
}`;

const FS_COMPOSITE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
uniform sampler2D u_bloom;
uniform float u_time;
uniform vec2 u_resolution;
uniform float u_crt;
uniform float u_curvature;
uniform float u_bloom_amt;
uniform float u_chromatic;
uniform float u_grain;
uniform float u_vignette;
uniform float u_glow;
uniform float u_scanline_size;
out vec4 frag;

vec2 curve(vec2 uv, float amt){
  uv = uv * 2.0 - 1.0;
  vec2 off = abs(uv.yx) / vec2(6.0, 4.0) * amt;
  uv = uv + uv * off * off;
  return uv * 0.5 + 0.5;
}

float hash(vec2 p){
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p.yx + 19.19);
  return fract((p.x + p.y) * p.x);
}

void main(){
  vec2 uv = v_uv;
  if (u_curvature > 0.001) uv = curve(uv, u_curvature);
  vec2 borderMask = step(vec2(0.0), uv) * step(uv, vec2(1.0));
  float inBounds = borderMask.x * borderMask.y;

  // chromatic aberration: shift channels radially. Kept gentle near center so
  // UI outlines stay crisp; ramps with distance² toward the corners only.
  vec2 dir = uv - 0.5;
  float dist = length(dir);
  vec2 nDir = dist > 0.0001 ? dir / dist : vec2(0.0);
  float strength = u_chromatic * (0.0010 + 0.0060 * dist * dist);
  vec3 src;
  src.r = texture(u_src, uv + nDir * strength).r;
  src.g = texture(u_src, uv).g;
  src.b = texture(u_src, uv - nDir * strength).b;

  vec3 bloom;
  bloom.r = texture(u_bloom, uv + nDir * strength).r;
  bloom.g = texture(u_bloom, uv).g;
  bloom.b = texture(u_bloom, uv - nDir * strength).b;

  vec3 col = src + bloom * u_bloom_amt;

  // scanlines (intensity-tunable)
  float scan = 1.0;
  if (u_crt > 0.0) {
    float row = uv.y * u_resolution.y / max(1.0, u_scanline_size);
    float s = 0.5 + 0.5 * cos(row * 6.2831853);
    scan = mix(1.0, mix(0.65, 1.0, s), u_crt);
    // RGB triad / aperture grille hint
    float subPixel = mod(uv.x * u_resolution.x, 3.0);
    vec3 triad = vec3(
      subPixel < 1.0 ? 1.0 : 0.85,
      subPixel >= 1.0 && subPixel < 2.0 ? 1.0 : 0.85,
      subPixel >= 2.0 ? 1.0 : 0.85
    );
    col *= mix(vec3(1.0), triad, u_crt * 0.35);
  }
  col *= scan;

  // film grain
  float n = hash(uv * u_resolution + vec2(u_time, u_time * 1.3));
  col += (n - 0.5) * u_grain * 0.18;

  // vignette
  float vig = smoothstep(1.1, 0.35, dist);
  col *= mix(1.0, vig, u_vignette);

  // overall additive glow lift (very subtle, drives the "neon" feel)
  col += bloom * u_glow * 0.25;

  // out-of-bounds (curvature spill) — black
  col *= inBounds;

  frag = vec4(col, 1.0);
}`;

const FS_PASSTHROUGH = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_src;
out vec4 frag;
void main(){ frag = texture(u_src, v_uv); }`;

interface Program {
  prog: WebGLProgram;
  vao: WebGLVertexArrayObject;
  uniforms: Record<string, WebGLUniformLocation | null>;
}

interface RenderTarget {
  fb: WebGLFramebuffer;
  tex: WebGLTexture;
  w: number; h: number;
}

export class PostFx {
  private gl: WebGL2RenderingContext | null = null;
  private outCanvas: HTMLCanvasElement;
  private srcTex: WebGLTexture | null = null;
  private srcTexW = 0;
  private srcTexH = 0;
  private bright: Program | null = null;
  private blur: Program | null = null;
  private composite: Program | null = null;
  private passthrough: Program | null = null;
  private rtA: RenderTarget | null = null;
  private rtB: RenderTarget | null = null;
  private rtC: RenderTarget | null = null;
  private quadBuf: WebGLBuffer | null = null;
  private startedAt = performance.now();
  private failed = false;
  settings: PostFxSettings = { ...DEFAULT_POSTFX };

  constructor(outCanvas: HTMLCanvasElement) {
    this.outCanvas = outCanvas;
    this.tryInit();
  }

  available(): boolean { return !!this.gl && !this.failed; }

  private tryInit(): void {
    const gl = this.outCanvas.getContext('webgl2', { premultipliedAlpha: false, alpha: false, antialias: false });
    if (!gl) { this.failed = true; return; }
    this.gl = gl;
    this.bright = this.compileProgram(VS, FS_BRIGHT, ['a_pos', 'a_uv'], ['u_src', 'u_threshold']);
    this.blur = this.compileProgram(VS, FS_BLUR, ['a_pos', 'a_uv'], ['u_src', 'u_dir', 'u_texel']);
    this.composite = this.compileProgram(VS, FS_COMPOSITE, ['a_pos', 'a_uv'], [
      'u_src', 'u_bloom', 'u_time', 'u_resolution', 'u_crt', 'u_curvature', 'u_bloom_amt',
      'u_chromatic', 'u_grain', 'u_vignette', 'u_glow', 'u_scanline_size',
    ]);
    this.passthrough = this.compileProgram(VS, FS_PASSTHROUGH, ['a_pos', 'a_uv'], ['u_src']);
    if (!this.bright || !this.blur || !this.composite || !this.passthrough) {
      this.failed = true; return;
    }
    this.srcTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.srcTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  private compileProgram(
    vs: string, fs: string, attribs: string[], uniforms: string[],
  ): Program | null {
    const gl = this.gl!;
    const vsObj = this.compileShader(gl.VERTEX_SHADER, vs);
    const fsObj = this.compileShader(gl.FRAGMENT_SHADER, fs);
    if (!vsObj || !fsObj) return null;
    const prog = gl.createProgram();
    if (!prog) return null;
    gl.attachShader(prog, vsObj); gl.attachShader(prog, fsObj);
    attribs.forEach((n, i) => gl.bindAttribLocation(prog, i, n));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      // eslint-disable-next-line no-console
      console.error('link error', gl.getProgramInfoLog(prog));
      return null;
    }
    // Quad VBO (shared, created lazily once).
    if (!this.quadBuf) {
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      const verts = new Float32Array([
        -1, -1, 0, 0,
        1, -1, 1, 0,
        -1, 1, 0, 1,
        -1, 1, 0, 1,
        1, -1, 1, 0,
        1, 1, 1, 1,
      ]);
      gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
      this.quadBuf = buf;
    }
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
    const u: Record<string, WebGLUniformLocation | null> = {};
    for (const name of uniforms) u[name] = gl.getUniformLocation(prog, name);
    return { prog, vao: vao!, uniforms: u };
  }

  private compileShader(type: number, src: string): WebGLShader | null {
    const gl = this.gl!;
    const sh = gl.createShader(type);
    if (!sh) return null;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      // eslint-disable-next-line no-console
      console.error('shader compile error', gl.getShaderInfoLog(sh), src);
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  private makeRT(w: number, h: number): RenderTarget {
    const gl = this.gl!;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    return { fb, tex, w, h };
  }

  private resizeRTs(w: number, h: number): void {
    const gl = this.gl!;
    const halfW = Math.max(8, w >> 1);
    const halfH = Math.max(8, h >> 1);
    if (!this.rtA || this.rtA.w !== w || this.rtA.h !== h) {
      if (this.rtA) { gl.deleteFramebuffer(this.rtA.fb); gl.deleteTexture(this.rtA.tex); }
      this.rtA = this.makeRT(w, h);
    }
    if (!this.rtB || this.rtB.w !== halfW || this.rtB.h !== halfH) {
      if (this.rtB) { gl.deleteFramebuffer(this.rtB.fb); gl.deleteTexture(this.rtB.tex); }
      this.rtB = this.makeRT(halfW, halfH);
    }
    if (!this.rtC || this.rtC.w !== halfW || this.rtC.h !== halfH) {
      if (this.rtC) { gl.deleteFramebuffer(this.rtC.fb); gl.deleteTexture(this.rtC.tex); }
      this.rtC = this.makeRT(halfW, halfH);
    }
  }

  resize(w: number, h: number): void {
    if (!this.gl) return;
    if (this.outCanvas.width !== w || this.outCanvas.height !== h) {
      this.outCanvas.width = w; this.outCanvas.height = h;
    }
    this.gl.viewport(0, 0, w, h);
    this.resizeRTs(w, h);
  }

  render(srcCanvas: HTMLCanvasElement): void {
    if (!this.gl || !this.srcTex || !this.bright || !this.blur || !this.composite || !this.passthrough) return;
    if (!this.settings.enabled) {
      // passthrough
      this.uploadSource(srcCanvas);
      this.draw(this.passthrough, null, [{ tex: this.srcTex, unit: 0, name: 'u_src' }], (gl, uniforms) => {
        gl.uniform1i(uniforms['u_src']!, 0);
      });
      return;
    }
    const gl = this.gl;
    const w = this.outCanvas.width, h = this.outCanvas.height;
    this.uploadSource(srcCanvas);

    // 1. Bright pass → rtB (half res)
    this.draw(this.bright, this.rtB, [{ tex: this.srcTex, unit: 0, name: 'u_src' }], (gl, uniforms) => {
      gl.uniform1i(uniforms['u_src']!, 0);
      gl.uniform1f(uniforms['u_threshold']!, this.settings.bloomThreshold);
    });

    // 2. Blur horizontal: rtB → rtC
    this.draw(this.blur, this.rtC, [{ tex: this.rtB!.tex, unit: 0, name: 'u_src' }], (gl, uniforms) => {
      gl.uniform1i(uniforms['u_src']!, 0);
      gl.uniform2f(uniforms['u_dir']!, 1, 0);
      gl.uniform2f(uniforms['u_texel']!, 1 / this.rtB!.w, 1 / this.rtB!.h);
    });

    // 3. Blur vertical: rtC → rtB
    this.draw(this.blur, this.rtB, [{ tex: this.rtC!.tex, unit: 0, name: 'u_src' }], (gl, uniforms) => {
      gl.uniform1i(uniforms['u_src']!, 0);
      gl.uniform2f(uniforms['u_dir']!, 0, 1);
      gl.uniform2f(uniforms['u_texel']!, 1 / this.rtC!.w, 1 / this.rtC!.h);
    });

    // 4. Composite to default framebuffer
    this.draw(this.composite, null, [
      { tex: this.srcTex, unit: 0, name: 'u_src' },
      { tex: this.rtB!.tex, unit: 1, name: 'u_bloom' },
    ], (gl, uniforms) => {
      gl.uniform1i(uniforms['u_src']!, 0);
      gl.uniform1i(uniforms['u_bloom']!, 1);
      gl.uniform1f(uniforms['u_time']!, (performance.now() - this.startedAt) / 1000);
      gl.uniform2f(uniforms['u_resolution']!, w, h);
      gl.uniform1f(uniforms['u_crt']!, this.settings.crt);
      gl.uniform1f(uniforms['u_curvature']!, this.settings.curvature);
      gl.uniform1f(uniforms['u_bloom_amt']!, this.settings.bloom);
      gl.uniform1f(uniforms['u_chromatic']!, this.settings.chromatic);
      gl.uniform1f(uniforms['u_grain']!, this.settings.grain);
      gl.uniform1f(uniforms['u_vignette']!, this.settings.vignette);
      gl.uniform1f(uniforms['u_glow']!, this.settings.glow);
      gl.uniform1f(uniforms['u_scanline_size']!, this.settings.scanlineSize);
    });
  }

  private uploadSource(srcCanvas: HTMLCanvasElement): void {
    const gl = this.gl!;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.srcTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    const w = srcCanvas.width, h = srcCanvas.height;
    // Reallocate only on size change; reuse texSubImage2D for fast subsequent uploads.
    if (w !== this.srcTexW || h !== this.srcTexH) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, srcCanvas);
      this.srcTexW = w; this.srcTexH = h;
    } else {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, srcCanvas);
    }
  }

  private draw(
    prog: Program,
    target: RenderTarget | null,
    textures: { tex: WebGLTexture; unit: number; name: string }[],
    setUniforms: (gl: WebGL2RenderingContext, u: Record<string, WebGLUniformLocation | null>) => void,
  ): void {
    const gl = this.gl!;
    if (target) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fb);
      gl.viewport(0, 0, target.w, target.h);
    } else {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, this.outCanvas.width, this.outCanvas.height);
    }
    gl.useProgram(prog.prog);
    gl.bindVertexArray(prog.vao);
    for (const t of textures) {
      gl.activeTexture(gl.TEXTURE0 + t.unit);
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
    }
    setUniforms(gl, prog.uniforms);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
}
